/**
 * The upgrade shop. One button per upgrade, built once; each frame toggles
 * visibility (unlocked and unbought) and affordability. Bought upgrades move
 * to an Owned section below as check-marked tiles, so the player can see
 * what they have. Tooltips go through the shared viewport-positioned
 * tooltip so the scrolling panel can't clip them.
 */

import { UPGRADES } from '../data/upgrades';
import type { UpgradeDef } from '../data/upgrades';
import { describeCondition } from '../game/conditions';
import { hasUpgrade, isUpgradeAvailable, upgradesAllowed } from '../game/economy';
import { withSpeciesNames } from '../game/inventory';
import { formatEggs } from '../game/numbers';
import type { GameState } from '../game/state';
import { bindTooltip } from './tooltip';
import { artIdFor } from './sprites';
import { addRowText } from './itemView';
import type { RowText } from './itemView';
import { createDrawer, drawerElement } from './drawer';

/**
 * Each upgrade's 48px icon in public/art/icons/up/. The click and global
 * upgrades have their own (named after the id's second half: warm-hands.png);
 * the dinosaur doublers show that dinosaur, with a "x2" badge drawn by the
 * tile, so the thirty templated upgrades need no art of their own.
 */
function iconUrl(def: UpgradeDef): string {
  // Ids are "dino:<id>:<n>", "click:<name>", "global:<name>" or "offline:<name>".
  const key = def.id.split(':')[1] ?? '';
  return `${import.meta.env.BASE_URL}art/icons/up/${artIdFor(key)}.png`;
}

function badgeText(def: UpgradeDef): string | null {
  return def.effect.kind === 'dinoMult' ? `×${def.effect.mult}` : null;
}

export interface UpgradesView {
  update(state: GameState, eps: number): void;
}

/** The state the tiles read; set on every update so names follow evolutions. */
let viewState: GameState | null = null;
const say = (text: string): string => (viewState ? withSpeciesNames(text, viewState) : text);

interface Row {
  def: UpgradeDef;
  /** The words the tile shows as a row (owned tiles stay icons). */
  text: RowText;
  button: HTMLButtonElement;
  /** The same icon as a non-interactive tile in the Owned section. */
  owned: HTMLElement;
  lastVisible: boolean | null;
  lastAffordable: boolean | null;
  lastOwned: boolean | null;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function iconImage(def: UpgradeDef): HTMLImageElement {
  const img = document.createElement('img');
  img.className = 'upgrade__icon';
  img.alt = '';
  img.src = iconUrl(def);
  return img;
}

function buildRow(def: UpgradeDef, onBuy: (def: UpgradeDef) => void): Row {
  const badge = badgeText(def);
  const icon = badge ? { src: iconUrl(def), badge } : { src: iconUrl(def) };

  const button = el('button', 'upgrade');
  button.type = 'button';
  button.hidden = true;
  // Icon only, like the achievement tiles; name, cost and effect live in
  // the tooltip. The accessible name carries the same for screen readers.
  button.setAttribute(
    'aria-label',
    `${def.name}, ${formatEggs(def.cost)} eggs: ${def.description}`,
  );
  button.append(iconImage(def));
  if (badge) button.append(el('span', 'upgrade__badge', badge));
  const text = addRowText(button, say(def.name), say(def.description), {
    currency: 'eggs',
    amount: formatEggs(def.cost),
  });
  button.addEventListener('click', () => onBuy(def));
  bindTooltip(button, () => ({
    icon,
    lines: [
      { text: say(def.name) },
      { text: `${formatEggs(def.cost)} eggs`, cost: true },
      { text: say(def.description), muted: true },
      { text: say(`Unlocked by: ${describeCondition(def.unlock)}`), muted: true },
    ],
  }));

  // Once bought it shows in the Owned section: the same tile, not a button.
  const owned = el('div', 'upgrade upgrade--owned');
  owned.tabIndex = 0;
  owned.hidden = true;
  owned.setAttribute('aria-label', `${def.name} (owned): ${def.description}`);
  owned.append(iconImage(def));
  if (badge) owned.append(el('span', 'upgrade__badge', badge));
  bindTooltip(owned, () => ({
    icon,
    // No "Owned" line: the tile is under the Owned heading already.
    lines: [{ text: say(def.name) }, { text: say(def.description), muted: true }],
  }));

  return {
    def,
    button,
    owned,
    text,
    lastVisible: null,
    lastAffordable: null,
    lastOwned: null,
  };
}

export function createUpgradesView(
  root: HTMLElement,
  onBuy: (def: UpgradeDef) => void,
): UpgradesView {
  const empty = el('p', 'upgrades__empty', 'Upgrades appear as you progress.');
  const list = el('div', 'upgrades__list');
  const ownedDrawer = createDrawer('Owned', 'upgrades');
  const ownedList = ownedDrawer.list;
  root.replaceChildren(empty, list, drawerElement(ownedDrawer));

  // Cheapest first, so the next thing to save for is always at the top.
  const rows = [...UPGRADES].sort((a, b) => a.cost - b.cost).map((def) => buildRow(def, onBuy));
  for (const row of rows) {
    list.append(row.button);
    ownedList.append(row.owned);
  }

  let lastAnyVisible: boolean | null = null;
  let lastAnyOwned: boolean | null = null;

  return {
    update(state, eps) {
      viewState = state;
      let anyVisible = false;
      let ownedCount = 0;
      // A Barren era: nothing to buy, and what is owned is dormant, so the
      // Owned drawer goes too.
      const allowed = upgradesAllowed(state);
      const emptyText = allowed
        ? 'Upgrades appear as you progress.'
        : 'No upgrades this era: it is a Barren one.';
      if (empty.textContent !== emptyText) empty.textContent = emptyText;
      for (const row of rows) {
        // Names follow evolutions ("Cozy Torosaurus nests"), rows included.
        const name = say(row.def.name);
        const description = say(row.def.description);
        row.text.setText(name, description);
        const owned = allowed && hasUpgrade(row.def, state);
        if (owned) ownedCount++;
        if (owned !== row.lastOwned) {
          row.lastOwned = owned;
          row.owned.hidden = !owned;
        }

        const visible = isUpgradeAvailable(row.def, state, eps);
        anyVisible ||= visible;
        if (visible !== row.lastVisible) {
          row.lastVisible = visible;
          row.button.hidden = !visible;
        }
        if (!visible) continue;

        const affordable = state.eggs >= row.def.cost;
        if (affordable !== row.lastAffordable) {
          row.lastAffordable = affordable;
          row.button.disabled = !affordable;
        }
      }
      if (anyVisible !== lastAnyVisible) {
        lastAnyVisible = anyVisible;
        empty.hidden = anyVisible;
      }
      const anyOwned = ownedCount > 0;
      if (anyOwned !== lastAnyOwned) {
        lastAnyOwned = anyOwned;
        ownedDrawer.setPresent(anyOwned);
      }
      ownedDrawer.setCount(ownedCount);
    },
  };
}
